import { type IDetail, type IRequestResult } from './models';
import { type IRequestConfig } from 'angular';

export class ApiError extends Error {
    public instanceName = 'ApiError';

    private result: IRequestResult;
    public errorCode: string;
    public backendMessage: string;

    constructor(requestResult: IRequestResult, requestConfig: IRequestConfig) {
        super('');
        this.result = requestResult;
        this.errorCode = requestResult.code || '';
        let error = '';
        if (this.result) {
            if (Array.isArray(this.result.details) && this.result.details.length > 0) {
                this.errorCode = this.result.details[0].code || '';
                error = this.result.details.map(detail => detail.localizedMessage).join('\n');
            } else if (this.result.data && this.result.data.error) {
                error = this.result.data.error_description || this.result.data.error;
            } else if (this.result.message) {
                error = this.result.localizedMessage || this.result.message;
            } else {
                error = typeof this.result === 'string' ? String(this.result) : JSON.stringify(this.result);
            }
        }
        this.message = error;

        if (!this.errorCode && this.result.code) {
            this.errorCode = this.result.code;
        }

        if (+this.errorCode >= 500) {
            this.instanceName = 'ApiError5xx';

            if (!requestConfig) {
                this.instanceName = 'ApiError5xx';
                this.message = 'common.errors.' + this.errorCode;
            } else {
                let url = requestConfig.url;
                if (url.startsWith('middle')) {
                    url = url.replace('middle://', '');
                }

                const message = Object.assign(this.result.message, {
                    statusCode: this.errorCode,
                    method: requestConfig.method,
                    url: url,
                    params: requestConfig.params,
                    data: requestConfig.data
                });
                this.message = JSON.stringify(message);
                if (typeof requestResult.message === 'string') {
                    this.backendMessage = this.result.localizedMessage || this.result.message;
                } else {
                    this.backendMessage = this.result.message['localizedMessage'] || this.result.message['message'];
                }
            }
        }

        Error.captureStackTrace(this, this.constructor);
    }

    public mergeDetails(anotherError: ApiError) {
        anotherError.result.details.forEach(detail => {
            this.result.details.push(detail);
        });
    }

    public get errorCodes(): string[] {
        let ret = [];
        if (this.result) {
            if (Array.isArray(this.result.details) && this.result.details.length > 0) {
                ret = this.result.details.map(detail => detail.code);
            }
        }
        return ret;
    }

    public get data() {
        return this.result;
    }
}

/**
 * A generic failure with an optional backend-supplied detail, for call sites that don't
 * go through `ApiError`'s `$http`/`IRequestResult`-driven construction (e.g. a raw `fetch()`)
 * but still want `.message` to stay a stable i18n key while surfacing the real backend text.
 *
 * `.result.details` uses the same shape `PopupHelper.getErrorTextTemplate()` already
 * renders for `ApiError`, so `DialogManager.error()` displays it without any special-casing.
 */
export class GenericBackendError extends Error {
    public instanceName = 'GenericBackendError';

    public readonly result: { details: IDetail[] } | undefined;

    /**
     * @param message - Kept as the error's `.message`, typically an i18n translation key.
     * @param config.backendDetail - Human-readable text from the backend, if any. When
     * provided, it's exposed as `.result.details` for the error dialog to render.
     * @param config.translationKey - Stored alongside `backendDetail` as each detail's
     * `localizedMessage`, so callers that show `.result.details` retain the i18n key too.
     */
    constructor(message: string, config?: { backendDetail?: string; translationKey?: string }) {
        super(message);
        if (config?.backendDetail) {
            this.result = { details: [{ localizedMessage: config.translationKey, message: config.backendDetail }] };
        }
        Error.captureStackTrace(this, this.constructor);
    }
}

export class CancelRequestError extends Error {
    public instanceName = 'CancelRequestError';

    constructor() {
        super('Request was canceled on client side');

        Error.captureStackTrace(this, this.constructor);
    }

    public toString() {
        return this.message;
    }
}
